import type {
  AttendanceTodayResponse,
  AuditListResponse,
  BriefingSummary,
  MeResponse,
  CreateSubjectRequest,
  CreateSubstitutionRequest,
  SaveTimetableRequest,
  SubjectListResponse,
  SubjectRow,
  SubstitutionSummaryResponse,
  TimetableResponse,
  AudienceFilter,
  AudiencePreview,
  BroadcastListResponse,
  CreateBroadcastRequest,
  CreateTemplateRequest,
  TemplateListResponse,
  TemplateRow,
  CreateExamRequest,
  CreateExamSubjectRequest,
  CreateRolloverPlanRequest,
  ExamDetail,
  ExamListResponse,
  MarksSheetResponse,
  ReportCardData,
  RolloverListResponse,
  RolloverPlanDetail,
  SaveMarksRequest,
  UpdateRolloverActionRequest,
  ApplicationListResponse,
  ApplicationRow,
  BillListResponse,
  CreateApplicationRequest,
  CreateBillRequest,
  CreateInquiryRequest,
  CreateQuoteRequest,
  CreateRequisitionRequest,
  InquiryListResponse,
  IssueTCRequest,
  MembershipListResponse,
  RecordBillPaymentRequest,
  RequisitionDetail,
  RequisitionListResponse,
  TCListResponse,
  TransferCertificateRow,
  UpdateApplicationStatusRequest,
  UpdateInquiryRequest,
  UpdateMembershipRequest,
  UpdateRequisitionStatusRequest,
  AssetListResponse,
  CreateAssetRequest,
  CreateRouteRequest,
  CreateTicketRequest,
  CreateVehicleRequest,
  GuardianAbsenceRequest,
  GuardianAttendanceResponse,
  GuardianBroadcastsResponse,
  GuardianDocumentsResponse,
  GuardianExamDetailResponse,
  GuardianFeesHistoryResponse,
  GuardianFeesResponse,
  GuardianHomeResponse,
  GuardianResultsResponse,
  GuardianTimetableResponse,
  GuardianWardSummary,
  GuardianYearResponse,
  CreatePettyCashRequest,
  PettyCashListResponse,
  VoidPettyCashRequest,
  CallLogListResponse,
  CreateCallLogRequest,
  UpdateCallOutcomeRequest,
  CreateDiaryNoteRequest,
  DiaryNoteListResponse,
  ExamRemarksResponse,
  TeacherGradebookResponse,
  UpsertExamRemarkRequest,
  CreateProgramRequest,
  CreateRoomRequest,
  ProgramListResponse,
  RoomListResponse,
  UpdateProgramRequest,
  UpdateRoomRequest,
  UpdateSubjectRequest,
  GuardianDetailResponse,
  StaffDetailResponse,
  AdmissionSettingsPayload,
  AdmissionSettingsResponse,
  AllocationPlanResponse,
  FinalizeMeritRequest,
  MeritListResponse,
  PlanAllocationRequest,
  ScoreApplicationRequest,
  ApplicantListResponse,
  AssetDetailResponse,
  CreateApplicantRequest,
  CreateInventoryItemRequest,
  CreateJobOpeningRequest,
  InventoryItemDetailResponse,
  InventoryListResponse,
  JobOpeningListResponse,
  RecordInventoryMovementRequest,
  UpdateApplicantStatusRequest,
  UpdateInventoryItemRequest,
  UpdateJobOpeningRequest,
  AssignStudentRouteRequest,
  GuardianTransportResponse,
  StudentRouteListResponse,
  BroadcastDetailResponse,
  ConsentDetailResponse,
  ConsentTopicListResponse,
  CreateConsentTopicRequest,
  IssuedCertVerification,
  SubmitConsentRequest,
  UpdateConsentTopicRequest,
  CreateRefundRequest,
  RefundListResponse,
  RefundableReceipt,
  GuardianConsentsResponse,
  GuardianConsentSubmitRequest,
  InvitePersonRequest,
  PeopleAccessResponse,
  CreateCustomFieldRequest,
  CustomFieldListResponse,
  UpdateCustomFieldRequest,
  TeacherProfileResponse,
  TeacherPayslipsResponse,
  TeacherRosterResponse,
  TeacherTodayResponse,
  TeacherWeekResponse,
  LeaveConsequenceResponse,
  TicketListResponse,
  TransportSummary,
  UpdateTicketStatusRequest,
  CheckInVisitorRequest,
  GatePassListResponse,
  GeneratePayrollRunRequest,
  IssueGatePassRequest,
  PayrollRunDetail,
  PayrollRunListResponse,
  SalaryStructureListResponse,
  UpdatePayrollRunStatusRequest,
  UpsertSalaryStructureRequest,
  VisitorListResponse,
  BonafideListResponse,
  BonafideRow,
  ConcessionListResponse,
  CreateConcessionRequest,
  IssueBonafideRequest,
  SearchResponse,
  StudentAttendanceResponse,
  StudentDocumentsResponse,
  StudentFeesResponse,
  StudentHomeResponse,
  StudentMarksResponse,
  StudentTimetableResponse,
  CreateHomeworkRequest,
  GradeHomeworkRequest,
  HomeworkDetail,
  HomeworkListResponse,
  StudentHomeworkResponse,
  CreateLeaveRequest,
  DecideLeaveRequest,
  LeaveBalancesResponse,
  LeaveRequestListResponse,
  LeaveStatus,
  SetLeaveBalancesRequest,
  CreateThreadRequest,
  SendMessageRequest,
  ThreadDetail,
  ThreadListResponse,
  SubmitHomeworkRequest,
  UpdateHomeworkStatusRequest,
  BulkCreateStudentsRequest,
  BulkImportResult,
  CalendarSummary,
  CreateFeeHeadRequest,
  CreateGradeRequest,
  CreateHolidayRequest,
  CreateReceiptRequest,
  CreateSectionRequest,
  CreateSessionRequest,
  CreateStaffRequest,
  CreateStudentRequest,
  CreateStudentResponse,
  CreateTermRequest,
  FeeHeadListResponse,
  GenerateInvoicesRequest,
  GenerateInvoicesResponse,
  GuardianDirectoryResponse,
  InstitutionContext,
  InvoiceListQuery,
  InvoiceListResponse,
  MarkAttendanceRequest,
  MarkAttendanceResponse,
  MarkStepDoneRequest,
  ReceiptListQuery,
  ReceiptListResponse,
  RegisterResponse,
  SetAttendanceModeRequest,
  SetupProgressResponse,
  StaffListResponse,
  StaffRow,
  StructureSummary,
  StudentDetail,
  StudentListQuery,
  StudentListResponse,
} from '@velora/contracts';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message?: string,
  ) {
    super(message ?? `API ${status}`);
    this.name = 'ApiError';
  }
}

/**
 * Pull a user-facing message out of any thrown value, prioritising the
 * server's `{ message }` body over the Error's own message. Use in mutation
 * onError handlers when you need the string. Form modals use `useVForm`
 * which handles this internally — this helper is for the non-form paths
 * (list-page status flips, delete confirmations, etc.).
 */
export function extractApiMessage(err: unknown): string | null {
  if (!err) return null;
  if (err instanceof ApiError) {
    const body = err.body as { message?: string } | null;
    return body?.message ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return null;
}

// See accounts/api.ts for the reason. Blank in dev, absolute in prod builds.
const API_BASE = (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? '';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json');
  const res = await fetch(`${API_BASE}/api${path}`, { ...init, headers, credentials: 'include' });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

export const productApi = {
  getInstitution(slug: string): Promise<InstitutionContext> {
    return request<InstitutionContext>(`/product/institutions/${encodeURIComponent(slug)}`);
  },
  getMe(): Promise<MeResponse> {
    return request<MeResponse>('/accounts/me');
  },
  logout(): Promise<{ ok: true }> {
    return request<{ ok: true }>('/accounts/logout', { method: 'POST' });
  },
  listStudents(slug: string, query: StudentListQuery = {}): Promise<StudentListResponse> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v) qs.set(k, String(v));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<StudentListResponse>(
      `/product/institutions/${encodeURIComponent(slug)}/students${suffix}`,
    );
  },
  getStudent(slug: string, admissionNumber: string): Promise<StudentDetail> {
    return request<StudentDetail>(
      `/product/institutions/${encodeURIComponent(slug)}/students/${encodeURIComponent(admissionNumber)}`,
    );
  },
  createStudent(slug: string, input: CreateStudentRequest): Promise<CreateStudentResponse> {
    return request<CreateStudentResponse>(
      `/product/institutions/${encodeURIComponent(slug)}/students`,
      { method: 'POST', body: JSON.stringify(input) },
    );
  },

  // Setup progress
  getSetupProgress(slug: string): Promise<SetupProgressResponse> {
    return request<SetupProgressResponse>(`/product/institutions/${encodeURIComponent(slug)}/setup/progress`);
  },
  markStepDone(slug: string, input: MarkStepDoneRequest): Promise<{ step: string; completed_at: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/setup/progress`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Calendar (step 2)
  getCalendar(slug: string): Promise<CalendarSummary> {
    return request<CalendarSummary>(`/product/institutions/${encodeURIComponent(slug)}/calendar`);
  },
  createSession(slug: string, input: CreateSessionRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/calendar/sessions`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  createTerm(slug: string, input: CreateTermRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/calendar/terms`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  createHoliday(slug: string, input: CreateHolidayRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/calendar/holidays`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Structure extras — programs, rooms, subject updates
  listPrograms(slug: string): Promise<ProgramListResponse> {
    return request<ProgramListResponse>(`/product/institutions/${encodeURIComponent(slug)}/programs`);
  },
  createProgram(slug: string, input: CreateProgramRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/programs`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  updateProgram(slug: string, id: string, input: UpdateProgramRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/programs/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },
  listRooms(slug: string): Promise<RoomListResponse> {
    return request<RoomListResponse>(`/product/institutions/${encodeURIComponent(slug)}/rooms`);
  },
  createRoom(slug: string, input: CreateRoomRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/rooms`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  updateRoom(slug: string, id: string, input: UpdateRoomRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/rooms/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },
  updateSubject(slug: string, id: string, input: UpdateSubjectRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/subjects/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },
  deleteSubject(slug: string, id: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/subjects/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // People detail
  getGuardianDetail(slug: string, id: string): Promise<GuardianDetailResponse> {
    return request<GuardianDetailResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardians/${encodeURIComponent(id)}`);
  },
  getStaffDetail(slug: string, id: string): Promise<StaffDetailResponse> {
    return request<StaffDetailResponse>(`/product/institutions/${encodeURIComponent(slug)}/staff/${encodeURIComponent(id)}`);
  },

  // Admissions extras
  scoreApplication(slug: string, code: string, input: ScoreApplicationRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/applications/${encodeURIComponent(code)}/score`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },
  getMerit(slug: string, grade?: string): Promise<MeritListResponse> {
    const qs = grade ? `?grade=${encodeURIComponent(grade)}` : '';
    return request<MeritListResponse>(`/product/institutions/${encodeURIComponent(slug)}/admissions/merit${qs}`);
  },
  finalizeMerit(slug: string, input: FinalizeMeritRequest): Promise<{ ok: true; ranked: number; finalized_at: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/admissions/merit/finalize`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  planAllocation(slug: string, input: PlanAllocationRequest): Promise<AllocationPlanResponse> {
    return request<AllocationPlanResponse>(`/product/institutions/${encodeURIComponent(slug)}/admissions/allocation/plan`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  getAdmissionSettings(slug: string): Promise<AdmissionSettingsResponse> {
    return request<AdmissionSettingsResponse>(`/product/institutions/${encodeURIComponent(slug)}/admissions/settings`);
  },
  updateAdmissionSettings(slug: string, input: AdmissionSettingsPayload): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/admissions/settings`, {
      method: 'PUT', body: JSON.stringify(input),
    });
  },

  // Asset detail (existing list at listAssets in estate.ts)
  getAssetDetail(slug: string, code: string): Promise<AssetDetailResponse> {
    return request<AssetDetailResponse>(`/product/institutions/${encodeURIComponent(slug)}/assets/${encodeURIComponent(code)}`);
  },

  // Inventory
  listInventory(slug: string): Promise<InventoryListResponse> {
    return request<InventoryListResponse>(`/product/institutions/${encodeURIComponent(slug)}/inventory`);
  },
  createInventoryItem(slug: string, input: CreateInventoryItemRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/inventory`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  updateInventoryItem(slug: string, id: string, input: UpdateInventoryItemRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/inventory/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },
  getInventoryItem(slug: string, id: string): Promise<InventoryItemDetailResponse> {
    return request<InventoryItemDetailResponse>(`/product/institutions/${encodeURIComponent(slug)}/inventory/${encodeURIComponent(id)}`);
  },
  recordInventoryMovement(slug: string, id: string, input: RecordInventoryMovementRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/inventory/${encodeURIComponent(id)}/movements`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },

  // Recruitment
  listJobOpenings(slug: string): Promise<JobOpeningListResponse> {
    return request<JobOpeningListResponse>(`/product/institutions/${encodeURIComponent(slug)}/recruitment/openings`);
  },
  createJobOpening(slug: string, input: CreateJobOpeningRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/recruitment/openings`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  updateJobOpening(slug: string, id: string, input: UpdateJobOpeningRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/recruitment/openings/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },
  listApplicants(slug: string, opts: { opening_id?: string; status?: string } = {}): Promise<ApplicantListResponse> {
    const p = new URLSearchParams();
    if (opts.opening_id) p.set('opening_id', opts.opening_id);
    if (opts.status) p.set('status', opts.status);
    const qs = p.toString() ? `?${p.toString()}` : '';
    return request<ApplicantListResponse>(`/product/institutions/${encodeURIComponent(slug)}/recruitment/applicants${qs}`);
  },
  createApplicant(slug: string, input: CreateApplicantRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/recruitment/applicants`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  updateApplicantStatus(slug: string, id: string, input: UpdateApplicantStatusRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/recruitment/applicants/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },

  // Transport assignments
  listTransportAssignments(slug: string, routeId?: string): Promise<StudentRouteListResponse> {
    const qs = routeId ? `?route_id=${encodeURIComponent(routeId)}` : '';
    return request<StudentRouteListResponse>(`/product/institutions/${encodeURIComponent(slug)}/transport/assignments${qs}`);
  },
  assignStudentToRoute(slug: string, input: AssignStudentRouteRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/transport/assignments`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  removeStudentFromRoute(slug: string, studentId: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/transport/assignments/${encodeURIComponent(studentId)}`, {
      method: 'DELETE',
    });
  },
  getGuardianTransport(slug: string, adm: string): Promise<GuardianTransportResponse> {
    return request<GuardianTransportResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/transport`);
  },

  // Comms detail + cert verifier
  getBroadcastDetail(slug: string, id: string): Promise<BroadcastDetailResponse> {
    return request<BroadcastDetailResponse>(`/product/institutions/${encodeURIComponent(slug)}/broadcasts/${encodeURIComponent(id)}`);
  },
  listConsentTopics(slug: string): Promise<ConsentTopicListResponse> {
    return request<ConsentTopicListResponse>(`/product/institutions/${encodeURIComponent(slug)}/consents`);
  },
  createConsentTopic(slug: string, input: CreateConsentTopicRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/consents`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  updateConsentTopic(slug: string, code: string, input: UpdateConsentTopicRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/consents/${encodeURIComponent(code)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },
  getConsentDetail(slug: string, code: string): Promise<ConsentDetailResponse> {
    return request<ConsentDetailResponse>(`/product/institutions/${encodeURIComponent(slug)}/consents/${encodeURIComponent(code)}`);
  },
  submitConsent(slug: string, code: string, input: SubmitConsentRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/consents/${encodeURIComponent(code)}/responses`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  verifyCert(slug: string, number: string): Promise<IssuedCertVerification> {
    return request<IssuedCertVerification>(`/product/institutions/${encodeURIComponent(slug)}/certs/verify/${encodeURIComponent(number)}`);
  },

  // Refunds (M-02)
  listRefunds(slug: string): Promise<RefundListResponse> {
    return request<RefundListResponse>(`/product/institutions/${encodeURIComponent(slug)}/refunds`);
  },
  listRefundableReceipts(slug: string, q?: string): Promise<{ total: number; rows: RefundableReceipt[] }> {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    return request(`/product/institutions/${encodeURIComponent(slug)}/refunds/refundable${qs}`);
  },
  createRefund(slug: string, input: CreateRefundRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/refunds`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },

  // Guardian consents (per ward)
  getGuardianConsents(slug: string, adm: string): Promise<GuardianConsentsResponse> {
    return request<GuardianConsentsResponse>(
      `/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/consents`,
    );
  },
  submitGuardianConsent(slug: string, adm: string, input: GuardianConsentSubmitRequest): Promise<{ ok: true }> {
    return request(
      `/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/consents`,
      { method: 'POST', body: JSON.stringify(input) },
    );
  },

  // Admin settings — people access + custom fields
  listPeopleAccess(slug: string): Promise<PeopleAccessResponse> {
    return request<PeopleAccessResponse>(`/product/institutions/${encodeURIComponent(slug)}/settings/people`);
  },
  invitePerson(slug: string, input: InvitePersonRequest): Promise<{ membership_id: string; person_id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/settings/people/invite`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  setPersonMembershipStatus(slug: string, membershipId: string, status: 'active' | 'inactive'): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/settings/people/${encodeURIComponent(membershipId)}`, {
      method: 'PATCH', body: JSON.stringify({ status }),
    });
  },
  listCustomFields(slug: string, entity?: 'student' | 'staff' | 'guardian'): Promise<CustomFieldListResponse> {
    const qs = entity ? `?entity=${entity}` : '';
    return request<CustomFieldListResponse>(`/product/institutions/${encodeURIComponent(slug)}/settings/custom-fields${qs}`);
  },
  createCustomField(slug: string, input: CreateCustomFieldRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/settings/custom-fields`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  updateCustomField(slug: string, id: string, input: UpdateCustomFieldRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/settings/custom-fields/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },

  // Staff (step 3)
  listStaff(slug: string): Promise<StaffListResponse> {
    return request<StaffListResponse>(`/product/institutions/${encodeURIComponent(slug)}/staff`);
  },
  createStaff(slug: string, input: CreateStaffRequest): Promise<StaffRow> {
    return request<StaffRow>(`/product/institutions/${encodeURIComponent(slug)}/staff`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Fee heads (step 4)
  listFeeHeads(slug: string): Promise<FeeHeadListResponse> {
    return request<FeeHeadListResponse>(`/product/institutions/${encodeURIComponent(slug)}/fees/heads`);
  },
  createFeeHead(slug: string, input: CreateFeeHeadRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/fees/heads`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Attendance mode (step 5)
  setAttendanceMode(slug: string, input: SetAttendanceModeRequest): Promise<{ mode: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/attendance-mode`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Structure
  getStructure(slug: string): Promise<StructureSummary> {
    return request<StructureSummary>(`/product/institutions/${encodeURIComponent(slug)}/structure`);
  },
  createGrade(slug: string, input: CreateGradeRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/structure/grades`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  deleteGrade(slug: string, id: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/structure/grades/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
  createSection(slug: string, input: CreateSectionRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/structure/sections`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  deleteSection(slug: string, id: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/structure/sections/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },

  // Deletes + session set-current
  deleteSession(slug: string, id: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/calendar/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  setSessionCurrent(slug: string, id: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/calendar/sessions/${encodeURIComponent(id)}/set-current`, { method: 'POST' });
  },
  deleteTerm(slug: string, id: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/calendar/terms/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  deleteHoliday(slug: string, id: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/calendar/holidays/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  deleteStaff(slug: string, id: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/staff/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  deleteFeeHead(slug: string, id: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/fees/heads/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  deleteStudent(slug: string, admissionNumber: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/students/${encodeURIComponent(admissionNumber)}`, { method: 'DELETE' });
  },

  // Briefing + guardians directory
  getBriefing(slug: string): Promise<BriefingSummary> {
    return request<BriefingSummary>(`/product/institutions/${encodeURIComponent(slug)}/briefing`);
  },
  listGuardians(slug: string, q?: string): Promise<GuardianDirectoryResponse> {
    const qs = q ? `?q=${encodeURIComponent(q)}` : '';
    return request<GuardianDirectoryResponse>(
      `/product/institutions/${encodeURIComponent(slug)}/guardians${qs}`,
    );
  },

  // Bulk student import
  bulkImportStudents(slug: string, input: BulkCreateStudentsRequest): Promise<BulkImportResult> {
    return request<BulkImportResult>(`/product/institutions/${encodeURIComponent(slug)}/students/bulk`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Fees ledger
  generateInvoices(slug: string, input: GenerateInvoicesRequest): Promise<GenerateInvoicesResponse> {
    return request<GenerateInvoicesResponse>(`/product/institutions/${encodeURIComponent(slug)}/invoices/generate`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  listInvoices(slug: string, query: InvoiceListQuery = {}): Promise<InvoiceListResponse> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v) qs.set(k, String(v));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<InvoiceListResponse>(`/product/institutions/${encodeURIComponent(slug)}/invoices${suffix}`);
  },
  createReceipt(slug: string, invoiceId: string, input: CreateReceiptRequest): Promise<{ id: string; receipt_number: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/invoices/${encodeURIComponent(invoiceId)}/receipts`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  listReceipts(slug: string, query: ReceiptListQuery = {}): Promise<ReceiptListResponse> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) if (v) qs.set(k, String(v));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<ReceiptListResponse>(`/product/institutions/${encodeURIComponent(slug)}/receipts${suffix}`);
  },

  // Attendance
  getAttendanceToday(slug: string, date?: string): Promise<AttendanceTodayResponse> {
    const qs = date ? `?date=${encodeURIComponent(date)}` : '';
    return request<AttendanceTodayResponse>(`/product/institutions/${encodeURIComponent(slug)}/attendance/today${qs}`);
  },
  getRegister(slug: string, date: string, grade: string, section: string): Promise<RegisterResponse> {
    const qs = new URLSearchParams({ date, grade, section }).toString();
    return request<RegisterResponse>(`/product/institutions/${encodeURIComponent(slug)}/attendance/register?${qs}`);
  },
  markAttendance(slug: string, input: MarkAttendanceRequest): Promise<MarkAttendanceResponse> {
    return request<MarkAttendanceResponse>(`/product/institutions/${encodeURIComponent(slug)}/attendance/mark`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Timetable + subjects
  listSubjects(slug: string): Promise<SubjectListResponse> {
    return request<SubjectListResponse>(`/product/institutions/${encodeURIComponent(slug)}/subjects`);
  },
  createSubject(slug: string, input: CreateSubjectRequest): Promise<SubjectRow> {
    return request<SubjectRow>(`/product/institutions/${encodeURIComponent(slug)}/subjects`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  getTimetable(slug: string, grade: string, section: string): Promise<TimetableResponse> {
    const qs = new URLSearchParams({ grade, section }).toString();
    return request<TimetableResponse>(`/product/institutions/${encodeURIComponent(slug)}/timetable?${qs}`);
  },
  saveTimetable(slug: string, input: SaveTimetableRequest): Promise<{ saved: number }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/timetable`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  getSubstitutions(slug: string, date: string, absent: string[]): Promise<SubstitutionSummaryResponse> {
    const qs = new URLSearchParams({ date, absent: absent.join(',') }).toString();
    return request<SubstitutionSummaryResponse>(`/product/institutions/${encodeURIComponent(slug)}/substitutions?${qs}`);
  },
  getFreeTeachers(slug: string, day: number, period: number): Promise<{ free: Array<{ id: string; name: string; role: string }> }> {
    const qs = new URLSearchParams({ day: String(day), period: String(period) }).toString();
    return request(`/product/institutions/${encodeURIComponent(slug)}/substitutions/free-teachers?${qs}`);
  },
  createSubstitution(slug: string, input: CreateSubstitutionRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/substitutions`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Comms
  listTemplates(slug: string): Promise<TemplateListResponse> {
    return request<TemplateListResponse>(`/product/institutions/${encodeURIComponent(slug)}/comms/templates`);
  },
  createTemplate(slug: string, input: CreateTemplateRequest): Promise<TemplateRow> {
    return request<TemplateRow>(`/product/institutions/${encodeURIComponent(slug)}/comms/templates`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  previewAudience(slug: string, filter: AudienceFilter): Promise<AudiencePreview> {
    return request<AudiencePreview>(`/product/institutions/${encodeURIComponent(slug)}/comms/audience/preview`, {
      method: 'POST',
      body: JSON.stringify({ filter }),
    });
  },
  listBroadcasts(slug: string): Promise<BroadcastListResponse> {
    return request<BroadcastListResponse>(`/product/institutions/${encodeURIComponent(slug)}/comms/broadcasts`);
  },
  createBroadcast(slug: string, input: CreateBroadcastRequest): Promise<{ id: string; status: string; recipient_count: number }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/comms/broadcasts`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  // Exams + marks
  listExams(slug: string): Promise<ExamListResponse> {
    return request<ExamListResponse>(`/product/institutions/${encodeURIComponent(slug)}/exams`);
  },
  createExam(slug: string, input: CreateExamRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/exams`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  getExam(slug: string, id: string): Promise<ExamDetail> {
    return request<ExamDetail>(`/product/institutions/${encodeURIComponent(slug)}/exams/${encodeURIComponent(id)}`);
  },
  createExamSubject(slug: string, examId: string, input: CreateExamSubjectRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/exams/${encodeURIComponent(examId)}/subjects`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  publishExam(slug: string, id: string): Promise<{ status: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/exams/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  getMarksSheet(slug: string, examSubjectId: string): Promise<MarksSheetResponse> {
    return request<MarksSheetResponse>(`/product/institutions/${encodeURIComponent(slug)}/exam-subjects/${encodeURIComponent(examSubjectId)}/marks`);
  },
  saveMarks(slug: string, examSubjectId: string, input: SaveMarksRequest): Promise<{ updated: number }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/exam-subjects/${encodeURIComponent(examSubjectId)}/marks`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  },
  getReportCard(slug: string, examId: string, admissionNumber: string): Promise<ReportCardData> {
    const qs = new URLSearchParams({ exam_id: examId, admission_number: admissionNumber }).toString();
    return request<ReportCardData>(`/product/institutions/${encodeURIComponent(slug)}/report-card?${qs}`);
  },

  // Rollover
  listRollover(slug: string): Promise<RolloverListResponse> {
    return request<RolloverListResponse>(`/product/institutions/${encodeURIComponent(slug)}/rollover`);
  },
  createRollover(slug: string, input: CreateRolloverPlanRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/rollover`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  getRollover(slug: string, id: string): Promise<RolloverPlanDetail> {
    return request<RolloverPlanDetail>(`/product/institutions/${encodeURIComponent(slug)}/rollover/${encodeURIComponent(id)}`);
  },
  updateRolloverAction(slug: string, actionId: string, input: UpdateRolloverActionRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/rollover/actions/${encodeURIComponent(actionId)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    });
  },
  commitRollover(slug: string, id: string): Promise<{ status: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/rollover/${encodeURIComponent(id)}/commit`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  reverseRollover(slug: string, id: string): Promise<{ status: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/rollover/${encodeURIComponent(id)}/reverse`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  // Admissions
  listInquiries(slug: string): Promise<InquiryListResponse> {
    return request<InquiryListResponse>(`/product/institutions/${encodeURIComponent(slug)}/inquiries`);
  },
  createInquiry(slug: string, input: CreateInquiryRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/inquiries`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  updateInquiry(slug: string, id: string, input: UpdateInquiryRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/inquiries/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },
  listApplications(slug: string): Promise<ApplicationListResponse> {
    return request<ApplicationListResponse>(`/product/institutions/${encodeURIComponent(slug)}/applications`);
  },
  createApplication(slug: string, input: CreateApplicationRequest): Promise<{ id: string; code: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/applications`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  getApplication(slug: string, code: string): Promise<ApplicationRow> {
    return request<ApplicationRow>(`/product/institutions/${encodeURIComponent(slug)}/applications/${encodeURIComponent(code)}`);
  },
  updateApplicationStatus(slug: string, code: string, input: UpdateApplicationStatusRequest): Promise<{ ok: true; admitted_student_id: string | null }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/applications/${encodeURIComponent(code)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },

  // Expenses
  listRequisitions(slug: string): Promise<RequisitionListResponse> {
    return request<RequisitionListResponse>(`/product/institutions/${encodeURIComponent(slug)}/requisitions`);
  },
  createRequisition(slug: string, input: CreateRequisitionRequest): Promise<{ id: string; code: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/requisitions`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  getRequisition(slug: string, code: string): Promise<RequisitionDetail> {
    return request<RequisitionDetail>(`/product/institutions/${encodeURIComponent(slug)}/requisitions/${encodeURIComponent(code)}`);
  },
  updateRequisitionStatus(slug: string, code: string, input: UpdateRequisitionStatusRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/requisitions/${encodeURIComponent(code)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },
  addQuote(slug: string, code: string, input: CreateQuoteRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/requisitions/${encodeURIComponent(code)}/quotes`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  selectQuote(slug: string, code: string, quoteId: string): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/requisitions/${encodeURIComponent(code)}/quotes/${encodeURIComponent(quoteId)}/select`, {
      method: 'POST', body: JSON.stringify({}),
    });
  },
  listBills(slug: string): Promise<BillListResponse> {
    return request<BillListResponse>(`/product/institutions/${encodeURIComponent(slug)}/bills`);
  },
  createBill(slug: string, input: CreateBillRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/bills`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  payBill(slug: string, id: string, input: RecordBillPaymentRequest): Promise<{ ok: true; status: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/bills/${encodeURIComponent(id)}/pay`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },

  // Certificates (TC)
  listTCs(slug: string): Promise<TCListResponse> {
    return request<TCListResponse>(`/product/institutions/${encodeURIComponent(slug)}/certificates/tc`);
  },
  issueTC(slug: string, input: IssueTCRequest): Promise<{ id: string; tc_number: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/certificates/tc`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  getTC(slug: string, tcNumber: string): Promise<TransferCertificateRow> {
    return request<TransferCertificateRow>(`/product/institutions/${encodeURIComponent(slug)}/certificates/tc/${encodeURIComponent(tcNumber)}`);
  },

  // Memberships (Roles page)
  listMemberships(slug: string): Promise<MembershipListResponse> {
    return request<MembershipListResponse>(`/product/institutions/${encodeURIComponent(slug)}/memberships`);
  },
  updateMembership(slug: string, id: string, input: UpdateMembershipRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/memberships/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },

  // Assets + Maintenance
  listAssets(slug: string): Promise<AssetListResponse> {
    return request<AssetListResponse>(`/product/institutions/${encodeURIComponent(slug)}/assets`);
  },
  createAsset(slug: string, input: CreateAssetRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/assets`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  listTickets(slug: string): Promise<TicketListResponse> {
    return request<TicketListResponse>(`/product/institutions/${encodeURIComponent(slug)}/maintenance`);
  },
  createTicket(slug: string, input: CreateTicketRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/maintenance`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  updateTicketStatus(slug: string, id: string, input: UpdateTicketStatusRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/maintenance/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },

  // Transport
  getTransport(slug: string): Promise<TransportSummary> {
    return request<TransportSummary>(`/product/institutions/${encodeURIComponent(slug)}/transport`);
  },
  createVehicle(slug: string, input: CreateVehicleRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/transport/vehicles`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  createRoute(slug: string, input: CreateRouteRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/transport/routes`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },

  // Teacher surface
  getTeacherToday(slug: string, date?: string): Promise<TeacherTodayResponse> {
    const qs = date ? `?date=${encodeURIComponent(date)}` : '';
    return request<TeacherTodayResponse>(`/product/institutions/${encodeURIComponent(slug)}/teacher/today${qs}`);
  },
  getTeacherProfile(slug: string): Promise<TeacherProfileResponse> {
    return request<TeacherProfileResponse>(`/product/institutions/${encodeURIComponent(slug)}/teacher/profile`);
  },
  getTeacherWeek(slug: string, start?: string): Promise<TeacherWeekResponse> {
    const qs = start ? `?start=${encodeURIComponent(start)}` : '';
    return request<TeacherWeekResponse>(`/product/institutions/${encodeURIComponent(slug)}/teacher/week${qs}`);
  },
  getTeacherRoster(slug: string, grade: string, section: string): Promise<TeacherRosterResponse> {
    return request<TeacherRosterResponse>(
      `/product/institutions/${encodeURIComponent(slug)}/teacher/class/${encodeURIComponent(grade)}/${encodeURIComponent(section)}`,
    );
  },
  getTeacherPayslips(slug: string): Promise<TeacherPayslipsResponse> {
    return request<TeacherPayslipsResponse>(`/product/institutions/${encodeURIComponent(slug)}/teacher/payslips`);
  },
  getLeaveConsequence(slug: string, from: string, to: string): Promise<LeaveConsequenceResponse> {
    return request<LeaveConsequenceResponse>(
      `/product/institutions/${encodeURIComponent(slug)}/leave/consequence?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
    );
  },
  listMyRequisitions(slug: string): Promise<RequisitionListResponse> {
    return request<RequisitionListResponse>(`/product/institutions/${encodeURIComponent(slug)}/requisitions?mine=true`);
  },

  // Guardian surface
  getGuardianHome(slug: string): Promise<GuardianHomeResponse> {
    return request<GuardianHomeResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/home`);
  },
  getGuardianWardSummary(slug: string, adm: string): Promise<GuardianWardSummary> {
    return request<GuardianWardSummary>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/summary`);
  },
  getGuardianAttendance(slug: string, adm: string, from?: string, to?: string): Promise<GuardianAttendanceResponse> {
    const params = new URLSearchParams();
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request<GuardianAttendanceResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/attendance${qs}`);
  },
  getGuardianFees(slug: string, adm: string): Promise<GuardianFeesResponse> {
    return request<GuardianFeesResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/fees`);
  },
  getGuardianBroadcasts(slug: string): Promise<GuardianBroadcastsResponse> {
    return request<GuardianBroadcastsResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/broadcasts`);
  },
  getGuardianTimetable(slug: string, adm: string): Promise<GuardianTimetableResponse> {
    return request<GuardianTimetableResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/timetable`);
  },
  getGuardianResults(slug: string, adm: string): Promise<GuardianResultsResponse> {
    return request<GuardianResultsResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/results`);
  },
  getGuardianExamDetail(slug: string, adm: string, examId: string): Promise<GuardianExamDetailResponse> {
    return request<GuardianExamDetailResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/results/${encodeURIComponent(examId)}`);
  },
  getGuardianDocuments(slug: string, adm: string): Promise<GuardianDocumentsResponse> {
    return request<GuardianDocumentsResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/documents`);
  },
  reportGuardianAbsence(slug: string, adm: string, input: GuardianAbsenceRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/absence`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  getGuardianFeesHistory(slug: string, adm: string): Promise<GuardianFeesHistoryResponse> {
    return request<GuardianFeesHistoryResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/fees/history`);
  },
  getGuardianYear(slug: string, adm: string, session?: string): Promise<GuardianYearResponse> {
    const qs = session ? `?session=${encodeURIComponent(session)}` : '';
    return request<GuardianYearResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/year${qs}`);
  },

  // Payroll
  getSalaryStructure(slug: string): Promise<SalaryStructureListResponse> {
    return request<SalaryStructureListResponse>(`/product/institutions/${encodeURIComponent(slug)}/payroll/structure`);
  },
  upsertSalary(slug: string, input: UpsertSalaryStructureRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/payroll/structure`, {
      method: 'PUT', body: JSON.stringify(input),
    });
  },
  listPayrollRuns(slug: string): Promise<PayrollRunListResponse> {
    return request<PayrollRunListResponse>(`/product/institutions/${encodeURIComponent(slug)}/payroll/runs`);
  },
  generatePayrollRun(slug: string, input: GeneratePayrollRunRequest): Promise<{ id: string; staff_count: number }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/payroll/runs`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  getPayrollRun(slug: string, month: string): Promise<PayrollRunDetail> {
    return request<PayrollRunDetail>(`/product/institutions/${encodeURIComponent(slug)}/payroll/runs/${encodeURIComponent(month)}`);
  },
  updatePayrollRunStatus(slug: string, month: string, input: UpdatePayrollRunStatusRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/payroll/runs/${encodeURIComponent(month)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },

  // Visitors + gate passes
  listVisitors(slug: string, date?: string): Promise<VisitorListResponse> {
    const qs = date ? `?date=${encodeURIComponent(date)}` : '';
    return request<VisitorListResponse>(`/product/institutions/${encodeURIComponent(slug)}/visitors${qs}`);
  },
  checkInVisitor(slug: string, input: CheckInVisitorRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/visitors`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  checkOutVisitor(slug: string, id: string): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/visitors/${encodeURIComponent(id)}/checkout`, {
      method: 'POST', body: JSON.stringify({}),
    });
  },
  listGatePasses(slug: string): Promise<GatePassListResponse> {
    return request<GatePassListResponse>(`/product/institutions/${encodeURIComponent(slug)}/gate-passes`);
  },
  issueGatePass(slug: string, input: IssueGatePassRequest): Promise<{ id: string; code: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/gate-passes`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  lookupGatePass(slug: string, code: string) {
    return request<import('@velora/contracts').GatePassRow>(
      `/product/institutions/${encodeURIComponent(slug)}/gate-passes/by-code/${encodeURIComponent(code)}`,
    );
  },
  useGatePass(slug: string, id: string): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/gate-passes/${encodeURIComponent(id)}/use`, {
      method: 'POST', body: JSON.stringify({}),
    });
  },
  revokeGatePass(slug: string, id: string): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/gate-passes/${encodeURIComponent(id)}/revoke`, {
      method: 'POST', body: JSON.stringify({}),
    });
  },

  // Concessions
  listConcessions(slug: string): Promise<ConcessionListResponse> {
    return request<ConcessionListResponse>(`/product/institutions/${encodeURIComponent(slug)}/concessions`);
  },
  createConcession(slug: string, input: CreateConcessionRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/concessions`, { method: 'POST', body: JSON.stringify(input) });
  },
  deleteConcession(slug: string, id: string): Promise<void> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/concessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // Bonafide certificates
  listBonafide(slug: string): Promise<BonafideListResponse> {
    return request<BonafideListResponse>(`/product/institutions/${encodeURIComponent(slug)}/certificates/bonafide`);
  },
  issueBonafide(slug: string, input: IssueBonafideRequest): Promise<{ id: string; cert_number: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/certificates/bonafide`, { method: 'POST', body: JSON.stringify(input) });
  },
  getBonafide(slug: string, certNumber: string): Promise<BonafideRow> {
    return request<BonafideRow>(`/product/institutions/${encodeURIComponent(slug)}/certificates/bonafide/${encodeURIComponent(certNumber)}`);
  },

  // Global search palette
  globalSearch(slug: string, q: string): Promise<SearchResponse> {
    return request<SearchResponse>(`/product/institutions/${encodeURIComponent(slug)}/search?q=${encodeURIComponent(q)}`);
  },

  // Student surface
  getStudentHome(slug: string): Promise<StudentHomeResponse> {
    return request<StudentHomeResponse>(`/product/institutions/${encodeURIComponent(slug)}/student/home`);
  },
  getStudentTimetable(slug: string): Promise<StudentTimetableResponse> {
    return request<StudentTimetableResponse>(`/product/institutions/${encodeURIComponent(slug)}/student/timetable`);
  },
  getStudentMarks(slug: string): Promise<StudentMarksResponse> {
    return request<StudentMarksResponse>(`/product/institutions/${encodeURIComponent(slug)}/student/marks`);
  },
  getStudentAttendance(slug: string, from?: string, to?: string): Promise<StudentAttendanceResponse> {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    const qs = p.toString() ? `?${p.toString()}` : '';
    return request<StudentAttendanceResponse>(`/product/institutions/${encodeURIComponent(slug)}/student/attendance${qs}`);
  },
  getStudentFees(slug: string): Promise<StudentFeesResponse> {
    return request<StudentFeesResponse>(`/product/institutions/${encodeURIComponent(slug)}/student/fees`);
  },
  getStudentDocuments(slug: string): Promise<StudentDocumentsResponse> {
    return request<StudentDocumentsResponse>(`/product/institutions/${encodeURIComponent(slug)}/student/documents`);
  },

  // Petty cash
  listPettyCash(slug: string, from?: string, to?: string): Promise<PettyCashListResponse> {
    const p = new URLSearchParams();
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    const qs = p.toString() ? `?${p.toString()}` : '';
    return request<PettyCashListResponse>(`/product/institutions/${encodeURIComponent(slug)}/petty-cash${qs}`);
  },
  createPettyCash(slug: string, input: CreatePettyCashRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/petty-cash`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  voidPettyCash(slug: string, id: string, input: VoidPettyCashRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/petty-cash/${encodeURIComponent(id)}/void`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },

  // Call log
  listCalls(slug: string, opts: { from?: string; to?: string; pending?: boolean } = {}): Promise<CallLogListResponse> {
    const p = new URLSearchParams();
    if (opts.from) p.set('from', opts.from);
    if (opts.to) p.set('to', opts.to);
    if (opts.pending) p.set('pending', 'true');
    const qs = p.toString() ? `?${p.toString()}` : '';
    return request<CallLogListResponse>(`/product/institutions/${encodeURIComponent(slug)}/calls${qs}`);
  },
  createCall(slug: string, input: CreateCallLogRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/calls`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  updateCallOutcome(slug: string, id: string, input: UpdateCallOutcomeRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/calls/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(input),
    });
  },

  // Teacher advanced
  getTeacherGradebook(slug: string): Promise<TeacherGradebookResponse> {
    return request<TeacherGradebookResponse>(`/product/institutions/${encodeURIComponent(slug)}/teacher/gradebook`);
  },
  listDiaryNotes(slug: string, opts: { grade?: string; section?: string; date?: string } = {}): Promise<DiaryNoteListResponse> {
    const p = new URLSearchParams();
    if (opts.grade) p.set('grade', opts.grade);
    if (opts.section) p.set('section', opts.section);
    if (opts.date) p.set('date', opts.date);
    const qs = p.toString() ? `?${p.toString()}` : '';
    return request<DiaryNoteListResponse>(`/product/institutions/${encodeURIComponent(slug)}/diary${qs}`);
  },
  createDiaryNote(slug: string, input: CreateDiaryNoteRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/diary`, {
      method: 'POST', body: JSON.stringify(input),
    });
  },
  getExamRemarks(slug: string, examId: string, opts: { grade?: string; section?: string } = {}): Promise<ExamRemarksResponse> {
    const p = new URLSearchParams();
    if (opts.grade) p.set('grade', opts.grade);
    if (opts.section) p.set('section', opts.section);
    const qs = p.toString() ? `?${p.toString()}` : '';
    return request<ExamRemarksResponse>(`/product/institutions/${encodeURIComponent(slug)}/exams/${encodeURIComponent(examId)}/remarks${qs}`);
  },
  upsertExamRemark(slug: string, examId: string, input: UpsertExamRemarkRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/exams/${encodeURIComponent(examId)}/remarks`, {
      method: 'PUT', body: JSON.stringify(input),
    });
  },

  // Homework
  listHomework(slug: string, params: { grade?: string; section?: string; mine?: boolean } = {}): Promise<HomeworkListResponse> {
    const p = new URLSearchParams();
    if (params.grade) p.set('grade', params.grade);
    if (params.section) p.set('section', params.section);
    if (params.mine) p.set('mine', 'true');
    const qs = p.toString() ? `?${p.toString()}` : '';
    return request<HomeworkListResponse>(`/product/institutions/${encodeURIComponent(slug)}/homework${qs}`);
  },
  createHomework(slug: string, input: CreateHomeworkRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/homework`, { method: 'POST', body: JSON.stringify(input) });
  },
  getHomework(slug: string, id: string): Promise<HomeworkDetail> {
    return request<HomeworkDetail>(`/product/institutions/${encodeURIComponent(slug)}/homework/${encodeURIComponent(id)}`);
  },
  updateHomeworkStatus(slug: string, id: string, input: UpdateHomeworkStatusRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/homework/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) });
  },
  submitHomework(slug: string, id: string, input: SubmitHomeworkRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/homework/${encodeURIComponent(id)}/submit`, { method: 'POST', body: JSON.stringify(input) });
  },
  gradeHomework(slug: string, id: string, input: GradeHomeworkRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/homework/${encodeURIComponent(id)}/grade`, { method: 'POST', body: JSON.stringify(input) });
  },
  getStudentHomework(slug: string): Promise<StudentHomeworkResponse> {
    return request<StudentHomeworkResponse>(`/product/institutions/${encodeURIComponent(slug)}/student/homework`);
  },
  getGuardianWardHomework(slug: string, adm: string): Promise<StudentHomeworkResponse> {
    return request<StudentHomeworkResponse>(`/product/institutions/${encodeURIComponent(slug)}/guardian/wards/${encodeURIComponent(adm)}/homework`);
  },

  // CSV export URLs (just build the URL; browser downloads via <a href>)
  exportUrl(slug: string, kind: 'students' | 'receipts' | 'payslips' | 'bills' | 'attendance', query: Record<string, string> = {}): string {
    const qs = new URLSearchParams(query).toString();
    return `/api/product/institutions/${encodeURIComponent(slug)}/exports/${kind}.csv${qs ? `?${qs}` : ''}`;
  },

  // Leave
  listLeaveRequests(
    slug: string,
    params: { status?: LeaveStatus; staff_id?: string; mine?: boolean } = {},
  ): Promise<LeaveRequestListResponse> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.staff_id) qs.set('staff_id', params.staff_id);
    if (params.mine) qs.set('mine', 'true');
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<LeaveRequestListResponse>(`/product/institutions/${encodeURIComponent(slug)}/leave/requests${suffix}`);
  },
  createLeaveRequest(slug: string, input: CreateLeaveRequest): Promise<{ id: string; days: number }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/leave/requests`, { method: 'POST', body: JSON.stringify(input) });
  },
  decideLeaveRequest(slug: string, id: string, input: DecideLeaveRequest): Promise<{ ok: true; status: LeaveStatus }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/leave/requests/${encodeURIComponent(id)}/decide`, { method: 'POST', body: JSON.stringify(input) });
  },
  cancelLeaveRequest(slug: string, id: string): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/leave/requests/${encodeURIComponent(id)}/cancel`, { method: 'POST' });
  },
  getLeaveBalances(slug: string, staffId?: string): Promise<LeaveBalancesResponse> {
    const suffix = staffId ? `?staff_id=${encodeURIComponent(staffId)}` : '';
    return request<LeaveBalancesResponse>(`/product/institutions/${encodeURIComponent(slug)}/leave/balances${suffix}`);
  },
  setLeaveBalances(slug: string, input: SetLeaveBalancesRequest): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/leave/balances`, { method: 'PUT', body: JSON.stringify(input) });
  },
  getMyLeaves(slug: string): Promise<{
    staff: { id: string; name: string };
    session: { id: string; label: string } | null;
    balances: LeaveBalancesResponse['balances'];
    requests: LeaveRequestListResponse['rows'];
  }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/staff/leaves`);
  },

  // Messaging
  listThreads(slug: string): Promise<ThreadListResponse> {
    return request<ThreadListResponse>(`/product/institutions/${encodeURIComponent(slug)}/threads`);
  },
  getThread(slug: string, id: string): Promise<ThreadDetail> {
    return request<ThreadDetail>(`/product/institutions/${encodeURIComponent(slug)}/threads/${encodeURIComponent(id)}`);
  },
  createThread(slug: string, input: CreateThreadRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/threads`, { method: 'POST', body: JSON.stringify(input) });
  },
  sendMessage(slug: string, threadId: string, input: SendMessageRequest): Promise<{ id: string }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/threads/${encodeURIComponent(threadId)}/messages`, { method: 'POST', body: JSON.stringify(input) });
  },
  markThreadRead(slug: string, threadId: string): Promise<{ ok: true }> {
    return request(`/product/institutions/${encodeURIComponent(slug)}/threads/${encodeURIComponent(threadId)}/read`, { method: 'POST' });
  },

  // Audit
  listAudit(slug: string, params: { entity_type?: string; action?: string; limit?: number } = {}): Promise<AuditListResponse> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v) qs.set(k, String(v));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<AuditListResponse>(`/product/institutions/${encodeURIComponent(slug)}/audit${suffix}`);
  },
};
